import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './Dashboard.css';
import './Admin.css';
import { getLabeledVideoUrl } from '../lib/videoUtils';
import BodyPatternTicks from '../components/BodyPatternTicks';

const LIMB_OPTIONS = [
  'Left Front', 'Right Front', 'Left Hind', 'Right Hind',
  'Both Front', 'Both Hind', 'All Limbs', 'Back/Pelvis'
];

const Dashboard = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [labels, setLabels] = useState({});
  const [labelForm, setLabelForm] = useState({});
  const [savingLabel, setSavingLabel] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      setProfile(profileData);

      const { data: horsesData } = await supabase
        .from('horses')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      setHorses(horsesData || []);

      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('*, analysis_reports(*), horses(name)')
        .eq('owner_id', user.id)
        .order('upload_date', { ascending: false })
        .limit(10);
      if (videosError) throw videosError;
      setVideos(videosData || []);

      // Load existing labels for this user's videos
      const videoIds = (videosData || []).map(v => v.id);
      if (videoIds.length > 0) {
        const { data: labelsData } = await supabase
          .from('training_labels')
          .select('*')
          .in('video_id', videoIds);
        const labelsMap = {};
        (labelsData || []).forEach(l => { labelsMap[l.video_id] = l; });
        setLabels(labelsMap);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
    setLoading(false);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const initLabelForm = (videoId, report) => {
    const existing = labels[videoId];
    if (existing) {
      setLabelForm(prev => ({
        ...prev,
        [videoId]: {
          is_correct: existing.is_correct,
          correct_result: existing.correct_result || (report?.is_abnormal ? 'abnormal' : 'normal'),
          correct_severity: existing.correct_severity || report?.overall_severity || 'normal',
          affected_limbs: existing.affected_limbs || [],
          notes: existing.notes || '',
        }
      }));
    } else {
      setLabelForm(prev => ({
        ...prev,
        [videoId]: {
          is_correct: null,
          correct_result: report?.is_abnormal ? 'abnormal' : 'normal',
          correct_severity: report?.overall_severity || 'normal',
          affected_limbs: [],
          notes: '',
        }
      }));
    }
  };

  const updateLabelField = (videoId, field, value) => {
    setLabelForm(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], [field]: value }
    }));
  };

  const toggleLimb = (videoId, limb) => {
    const current = labelForm[videoId]?.affected_limbs || [];
    const updated = current.includes(limb)
      ? current.filter(l => l !== limb)
      : [...current, limb];
    updateLabelField(videoId, 'affected_limbs', updated);
  };

  const saveLabel = async (videoId) => {
    const form = labelForm[videoId];
    if (form.is_correct === null) return;
    setSavingLabel(videoId);

    const existing = labels[videoId];
    const labelData = {
      video_id: videoId,
      reviewer_id: user.id,
      is_correct: form.is_correct,
      correct_result: form.is_correct ? null : form.correct_result,
      correct_severity: form.is_correct ? null : form.correct_severity,
      affected_limbs: form.is_correct ? null : form.affected_limbs,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (existing) {
      ({ error } = await supabase.from('training_labels').update(labelData).eq('id', existing.id));
    } else {
      ({ error } = await supabase.from('training_labels').insert(labelData));
    }

    if (error) {
      console.error('Error saving:', error);
      showToast('❌ Error saving feedback');
    } else {
      showToast('✅ Feedback saved!');
      await loadData();
    }
    setSavingLabel(null);
  };

  const getScoreColor = (score) => {
    if (score >= 96) return '#22c55e';
    if (score >= 92) return '#86efac';
    if (score >= 85) return '#eab308';
    if (score >= 75) return '#f97316';
    return '#ef4444';
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || 'there';

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {toast && <div className="admin-toast">{toast}</div>}
      <div className="dashboard-header">
        <h1>{getGreeting()}, {displayName}! 🐴</h1>
        <p>Here's what's happening with your horses</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🐎</div>
          <div className="stat-info">
            <h3>{horses.length}</h3>
            <p>Horses</p>
          </div>
        </div>
        <Link to="/videos" className="stat-card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="stat-icon">📹</div>
          <div className="stat-info">
            <h3>{videos.length}</h3>
            <p>Videos Uploaded</p>
          </div>
        </Link>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{videos.filter(v => v.analysis_reports?.length > 0).length}</h3>
            <p>Analyses Complete</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>
              {videos.filter(v =>
                v.analysis_reports?.some(r => !r.is_abnormal)
              ).length}
            </h3>
            <p>Normal Results</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/analyze" className="action-card">
            <span className="action-icon">📹</span>
            <span>Upload & Analyze Video</span>
          </Link>
          <Link to="/horses/new" className="action-card">
            <span className="action-icon">🐎</span>
            <span>Add a Horse</span>
          </Link>
          <Link to="/horses" className="action-card">
            <span className="action-icon">📋</span>
            <span>View My Horses</span>
          </Link>
        </div>
      </div>

      {/* Recent Videos */}
      <div className="section">
        <div className="section-header">
          <h2>Recent Videos</h2>
          <Link to="/analyze" className="section-link">Upload New →</Link>
        </div>

        {videos.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📹</span>
            <h3>No videos yet</h3>
            <p>Upload your first horse video to get started with AI stride analysis</p>
            <Link to="/analyze" className="auth-button" style={{ textDecoration: 'none', display: 'inline-block', padding: '10px 24px' }}>
              Upload Video
            </Link>
          </div>
        ) : (
          <div className="video-list">
            {videos.map((video) => {
              const report = video.analysis_reports?.[0];
              const isExpanded = expandedVideo === video.id;
              const label = labels[video.id];
              const form = labelForm[video.id];

              return (
                <div key={video.id} className={`video-item ${label ? (label.is_correct ? 'labeled-correct' : 'labeled-incorrect') : ''}`}>
                  <div className="video-item-main" onClick={() => {
                    const newId = isExpanded ? null : video.id;
                    setExpandedVideo(newId);
                    if (newId) initLabelForm(video.id, report);
                  }}>
                    <div className="video-icon">
                      {label ? (label.is_correct ? '✅' : '❌') : '🎬'}
                    </div>
                    <div className="video-details">
                      <h4>{video.horses?.name ? `${video.horses.name} — ` : ''}{video.filename}</h4>
                      <p className="video-meta">
                        {new Date(video.upload_date).toLocaleDateString()} •{' '}
                        {video.status === 'analyzed' ? 'Analyzed' : video.status}
                        {video.gait_type && ` • ${video.gait_type}`}
                        {label && <> • {label.is_correct ? '✅ Reviewed' : '❌ Flagged'}</>}
                      </p>
                    </div>
                    <div className="video-badges">
                      {report ? (
                        <span className="score-pill" style={{ backgroundColor: getScoreColor(report.soundness_score) }}>
                          {report.soundness_score}/100
                        </span>
                      ) : (
                        <span className="status-pill pending">⏳</span>
                      )}
                    </div>
                    <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>▸</span>
                  </div>

                  {isExpanded && (
                    <div className="video-expanded label-expanded">
                      <div className="label-video-container">
                        <video controls src={getLabeledVideoUrl(video)} className="label-video"
                          onLoadedMetadata={(e) => { e.target.playbackRate = 0.5; }} />
                      </div>

                      {report && (
                        <div className="ai-assessment">
                          <h5>🤖 AI Assessment</h5>
                          <p><strong>{report.is_abnormal ? '⚠️ Abnormal' : '✅ Normal'}</strong> — Score: {report.soundness_score}/100 — Severity: {report.overall_severity}</p>
                          {report.findings?.map((f, i) => (
                            <p key={i} className="ai-finding">• {f.title}: {f.description}</p>
                          ))}
                        </div>
                      )}

                      <BodyPatternTicks videoId={video.id} user={user} />

                      {form && (
                        <div className="label-form">
                          <h5>🏷️ Your Assessment</h5>

                          <div className="label-question">
                            <p>Is the AI's assessment correct?</p>
                            <div className="label-buttons">
                              <button className={`label-btn ${form.is_correct === true ? 'selected correct' : ''}`}
                                onClick={() => updateLabelField(video.id, 'is_correct', true)}>✅ Yes, Correct</button>
                              <button className={`label-btn ${form.is_correct === false ? 'selected incorrect' : ''}`}
                                onClick={() => updateLabelField(video.id, 'is_correct', false)}>❌ No, Incorrect</button>
                            </div>
                          </div>

                          {form.is_correct === false && (
                            <>
                              <div className="label-question">
                                <p>What is the correct result?</p>
                                <div className="label-buttons">
                                  <button className={`label-btn ${form.correct_result === 'normal' ? 'selected' : ''}`}
                                    onClick={() => updateLabelField(video.id, 'correct_result', 'normal')}>Normal</button>
                                  <button className={`label-btn ${form.correct_result === 'abnormal' ? 'selected' : ''}`}
                                    onClick={() => updateLabelField(video.id, 'correct_result', 'abnormal')}>Abnormal</button>
                                </div>
                              </div>

                              {form.correct_result === 'abnormal' && (
                                <>
                                  <div className="label-question">
                                    <p>Severity?</p>
                                    <div className="label-buttons">
                                      {['mild', 'moderate', 'severe'].map(s => (
                                        <button key={s} className={`label-btn ${form.correct_severity === s ? 'selected' : ''}`}
                                          onClick={() => updateLabelField(video.id, 'correct_severity', s)}>{s}</button>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="label-question">
                                    <p>Affected limbs? (select all that apply)</p>
                                    <div className="limb-grid">
                                      {LIMB_OPTIONS.map(limb => (
                                        <button key={limb} className={`limb-btn ${form.affected_limbs?.includes(limb) ? 'selected' : ''}`}
                                          onClick={() => toggleLimb(video.id, limb)}>{limb}</button>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          )}

                          <div className="label-question">
                            <p>Notes (what do you see?)</p>
                            <textarea
                              value={form.notes}
                              onChange={(e) => updateLabelField(video.id, 'notes', e.target.value)}
                              placeholder="e.g., Clear right front lameness at trot, shortened stride, head nod..."
                              rows={3}
                            />
                          </div>

                          <button className="save-label-btn"
                            onClick={() => saveLabel(video.id)}
                            disabled={form.is_correct === null || savingLabel === video.id}>
                            {savingLabel === video.id ? 'Saving...' : (label ? '💾 Update' : '💾 Save')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* My Horses */}
      <div className="section">
        <div className="section-header">
          <h2>My Horses</h2>
          <Link to="/horses" className="section-link">View All →</Link>
        </div>

        {horses.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🐎</span>
            <h3>No horses added yet</h3>
            <p>Add your horses to track their health and stride analysis over time</p>
            <Link to="/horses/new" className="auth-button" style={{ textDecoration: 'none', display: 'inline-block', padding: '10px 24px' }}>
              Add a Horse
            </Link>
          </div>
        ) : (
          <div className="horses-grid">
            {horses.slice(0, 4).map((horse) => (
              <div key={horse.id} className="horse-card">
                <div className="horse-avatar">🐴</div>
                <h4>{horse.name}</h4>
                <p>{horse.breed || 'Unknown breed'}</p>
                {horse.age && <p className="horse-age">{horse.age} years old</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
